import type { SignalForFilterProps } from './signal-for-filter.interface';
import { useSignals } from '@preact/signals-react/runtime';
import { createElement, Fragment, useMemo } from 'react';
import { SignalItem } from '../item/signal-item.component';

/**
 * Takes a signal with an array of items, filters out the items and calls a child render function for each item.
 * @param {Signal<Array<T>>} items - A signal containing an array of items to be filtered.
 * @param {(item: T) => boolean} filter - The filter function used to determine what to return.
 * @param {React.JSX.Element} fallback - The element to return when the filter returns no results.
 * @param {(items: Array<T>) => React.JSX.Element} children - The child render function called for each result.
 * @constructor
 */
const SignalForFilter = <T,>({ items, filter, fallback, children }: SignalForFilterProps<T>) => {
  useSignals();

  const cache = useMemo(() => new Map(), []);
  const list = items.value;

  if (!list.length) return fallback || null;

  const values = list.filter(filter).map((value, key) => {
    if (!cache.has(value)) {
      return (
        <SignalItem key={key} v={value} i={key} cache={cache}>
          {children}
        </SignalItem>
      );
    }

    return cache.get(value);
  });

  return createElement(Fragment, undefined, values);
};

export { SignalForFilter };
